<template>
  <div class="w-full">
    <button
      type="button"
      :class="buttonClass"
      :disabled="loading"
      @click="login"
    >
      <svg v-if="!loading" class="h-5 w-5 shrink-0" :class="iconSpacingClass" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.844-1.026 1.402-2.441 1.247-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.558-1.701z"/>
      </svg>
      <Loader v-if="loading" class="animate-spin h-5 w-5 shrink-0" :class="iconSpacingClass" />
      <span :class="labelClass">{{ loading ? $t('loading') + '...' : $t('auth.continue_with_apple') }}</span>
    </button>
    <div v-if="error" class="mt-2 text-red-500 text-sm text-center">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useConsentStore } from '@/stores/consent'
import { Loader } from 'lucide-vue-next'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getApiErrorMessage } from '@/utils/error-handler'

const props = defineProps({
  redirect: { type: String, default: null },
  /** Full path after sign-in (e.g. meal plan return URL). Takes precedence over `redirect`. */
  redirectPath: { type: String, default: null },
  /** Called immediately before Apple sign-in (e.g. save wizard draft). */
  beforeLogin: { type: Function, default: null },
  /** `default` = outlined auth style; `primary` = pill shape aligned with Google primary (meal plan gate). */
  variant: {
    type: String,
    default: 'default',
    validator: (v) => ['default', 'primary'].includes(v)
  }
})

const emit = defineEmits(['error'])

const { t } = useI18n()
const loading = ref(false)
const error = ref('')
const auth = useAuthStore()
const consentStore = useConsentStore()
const router = useRouter()

const isPrimary = computed(() => props.variant === 'primary')

const buttonClass = computed(() => {
  if (isPrimary.value) {
    return 'flex w-full cursor-pointer items-center justify-center rounded-full bg-gray-900 px-4 py-4 text-base font-semibold text-white shadow-lg transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white'
  }
  return 'w-full flex justify-center items-center py-3 px-4 rounded-lg border-2 border-gray-900 dark:border-gray-100 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 font-bold hover:bg-black dark:hover:bg-white cursor-pointer shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200'
})

const iconSpacingClass = computed(() => (isPrimary.value ? 'me-2.5' : 'mr-3'))

const labelClass = computed(() => (isPrimary.value ? 'text-base font-semibold' : 'text-lg'))

async function login() {
  // Only show Apple sign in on iOS or if Capacitor is available
  // The store handles the actual logic
  error.value = ''
  loading.value = true
  try {
    if (typeof props.beforeLogin === 'function') {
      await props.beforeLogin()
    }
    const user = await auth.loginWithApple()
    if (user) {
      await consentStore.trackLogin({ method: 'apple' })
      const dest = props.redirectPath || props.redirect
      if (dest) {
        router.push(dest)
      } else {
        router.push('/')
      }
    }
  } catch (err) {
    console.error('Apple login error:', err)
    error.value = getApiErrorMessage(err, t, 'auth.unknown_error')
    emit('error', error.value)
  } finally {
    loading.value = false
  }
}
</script>

